// Aufzeichnung eines Tourenberechnungslaufs.
//
// Der Recorder hängt bewusst an keinem Singleton und importiert keine PA-Klasse (nur Typen) —
// der Adapter in technician-date.ts schiebt ihm reines Plain-Data zu. Dadurch bleibt er zyklenfrei
// und im Unit-Test ohne Angular verwendbar.
//
// Aufgezeichnet wird bei JEDEM Lauf, nicht nur im Fehlerfall: Dispatcher melden Probleme auch dann,
// wenn keine Fehlermeldung kam, sondern das Ergebnis nur unplausibel aussah. Der Aufwand dafür sind
// ein paar Map-Schreibvorgänge pro Lauf.

import { Subject } from "rxjs";
import type { TourOptimizerOperation } from "./tour-optimizer.types";
import type {
  TourDebugCandidate,
  TourDebugCategories,
  TourDebugCoreCall,
  TourDebugDistance,
  TourDebugFrame,
  TourDebugHead,
  TourDebugRawOperation,
  TourDebugRejectionReason,
  TourDebugResult,
  TourDebugSnapshot,
  TourDebugStores,
} from "./tour-debug.types";
import { TOUR_DEBUG_FORMAT_VERSION } from "./tour-debug.types";

/** Obergrenzen, damit ein Lauf mit 720 Permutationen den Speicher nicht sprengt */
const MAX_CORE_CALLS = 12;
const MAX_REJECTED_CORE_CALLS = 6;
const MAX_CANDIDATES = 5;

/** Sammelt die Daten eines einzelnen Berechnungslaufs */
export class TourDebugRecording {
  private readonly startedAtMs: number;
  private frame: TourDebugFrame | null = null;
  private readonly operations = new Map<number, { raw: TourDebugRawOperation; core: TourOptimizerOperation }>();
  private readonly distances = new Map<string, TourDebugDistance>();
  private categories: TourDebugCategories | null = null;
  private stores: TourDebugStores | null = null;
  private permutationsTotal = 0;
  private permutationsScored = 0;
  private readonly rejections = new Map<TourDebugRejectionReason, number>();
  private readonly candidates: TourDebugCandidate[] = [];
  private readonly coreCalls: TourDebugCoreCall[] = [];
  private rejectedCoreCallCount = 0;
  private coreCallCounter = 0;

  constructor(
    public readonly head: TourDebugHead,
    /** Zeitstempel des Laufs; entspricht latestNewOperationUpdateTimestamp in PATechnicianDate */
    public readonly runTimestamp: number,
  ) {
    this.startedAtMs = head.recorded_at_ms;
  }

  setFrame(frame: TourDebugFrame): void {
    this.frame = frame;
  }

  /**
   * Auftrag festhalten. Wird pro Auftrag vielfach aufgerufen (einmal je Permutation), deshalb
   * gewinnt eine Fassung mit gesetzter Arbeitszeit gegen eine mit 0 — sonst landet im Wörterbuch
   * die Zwischenfassung aus der Umrechnung, in der die Arbeitszeit noch nicht nachgetragen war.
   */
  addOperation(raw: TourDebugRawOperation, core: TourOptimizerOperation): void {
    const existing = this.operations.get(core.id);
    if (existing && existing.core.work_time_ms > 0 && !core.work_time_ms) return;
    this.operations.set(core.id, { raw, core: { ...core } });
  }

  addDistance(from: string, to: string, use_cache: boolean, duration: number, distance: number): void {
    const key = `${from}|${to}|${use_cache}`;
    if (this.distances.has(key)) return;
    this.distances.set(key, { from, to, use_cache, duration, distance });
  }

  setCategories(categories: TourDebugCategories): void {
    this.categories = categories;
  }

  setStores(stores: TourDebugStores): void {
    this.stores = stores;
  }

  countPermutation(): void {
    this.permutationsTotal += 1;
  }

  countRejection(reason: TourDebugRejectionReason): void {
    this.rejections.set(reason, (this.rejections.get(reason) || 0) + 1);
  }

  addCandidate(candidate: TourDebugCandidate): void {
    this.permutationsScored += 1;
    this.candidates.push(candidate);
  }

  /**
   * Kern-Aufruf mitschneiden. Verworfene Aufrufe (Ergebnis null) sind für die Fehlersuche die
   * interessanten, deshalb haben sie ein eigenes Kontingent und verdrängen bei vollem Puffer einen
   * zuvor aufgezeichneten erfolgreichen Aufruf.
   */
  addCoreCall(call: Omit<TourDebugCoreCall, "index">): void {
    const index = this.coreCallCounter++;
    const is_rejected = call.result === null;

    if (is_rejected) {
      if (this.rejectedCoreCallCount >= MAX_REJECTED_CORE_CALLS) return;
      if (this.coreCalls.length >= MAX_CORE_CALLS) {
        const successful_idx = this.coreCalls.findIndex(existing => existing.result !== null);
        if (successful_idx < 0) return;
        this.coreCalls.splice(successful_idx, 1);
      }
      this.rejectedCoreCallCount += 1;
    } else if (this.coreCalls.length >= MAX_CORE_CALLS) {
      return;
    }

    this.coreCalls.push({ index, ...call });
  }

  /** Schließt die Aufzeichnung ab; ohne gesetzten Rahmen ist der Lauf nicht auswertbar */
  finish(result: Omit<TourDebugResult, "duration_ms">, finished_at_ms: number): TourDebugSnapshot | null {
    if (!this.frame) return null;

    const best_candidates = [...this.candidates]
      .sort((a, b) => {
        if (a.removed_operation_ids.length !== b.removed_operation_ids.length) {
          return a.removed_operation_ids.length - b.removed_operation_ids.length;
        }
        if (a.fixed_lateness_ms !== b.fixed_lateness_ms) return a.fixed_lateness_ms - b.fixed_lateness_ms;
        return (a.duration ?? Infinity) - (b.duration ?? Infinity);
      })
      .slice(0, MAX_CANDIDATES);

    return {
      format_version: TOUR_DEBUG_FORMAT_VERSION,
      head: this.head,
      frame: this.frame,
      operations: [...this.operations.values()].sort((a, b) => a.core.id - b.core.id),
      distances: [...this.distances.values()],
      flow: {
        categories: this.categories || emptyCategories(),
        stores: this.stores || { permutation_limit: 0, permuted: [], not_permuted: [], rejected_priority_operation_ids: [] },
        permutations_total: this.permutationsTotal,
        permutations_scored: this.permutationsScored,
        rejections: [...this.rejections.entries()].map(([reason, count]) => ({ reason, count })),
        candidates: best_candidates,
      },
      core_calls: [...this.coreCalls].sort((a, b) => a.index - b.index),
      result: { ...result, duration_ms: finished_at_ms - this.startedAtMs },
    };
  }
}

function emptyCategories(): TourDebugCategories {
  return {
    total: 0,
    already_started: 0,
    passed_fixed: 0,
    pinned_tour_index: 0,
    fixed_with_location_change: 0,
    same_day_sla_with_location_change: 0,
    normal_with_location_change: 0,
    fixed_without_location_change: 0,
    normal_without_location_change: 0,
  };
}

/**
 * Ablage der abgeschlossenen Aufzeichnungen. Hält je Technikertag die letzte Aufzeichnung, damit
 * das Debug-Fenster jederzeit — auch ohne vorherige Fehlermeldung — geöffnet werden kann.
 */
export class TourDebugStore {
  // Grenze über verschiedene Technikertage hinweg (je Tag wird ohnehin nur der letzte Lauf
  // gehalten). Großzügig gewählt, weil jede Timeline-Zeile einen eigenen Debug-Knopf hat: eine
  // knappe Grenze ließe Knöpfe noch sichtbarer Zeilen wieder ausgrauen.
  private static readonly MAX_ENTRIES = 100;
  private static entries = new Map<string, TourDebugSnapshot>();
  private static lastKey: string | null = null;

  /**
   * Feuert mit dem Schlüssel der Aufzeichnung, die angezeigt werden soll (oder null für die
   * jüngste). So kann eine Fehlermeldung das Debug-Fenster öffnen, ohne die Komponente zu kennen.
   */
  static readonly openRequests = new Subject<string | null>();

  static key(technician_id: number, day_timestamp: number): string {
    return `${technician_id}:${day_timestamp}`;
  }

  static save(snapshot: TourDebugSnapshot): void {
    const key = this.key(snapshot.head.technician_id, snapshot.head.day_timestamp);
    // Neu einsortieren, damit die Einfügereihenfolge der Map der Aktualität entspricht
    this.entries.delete(key);
    this.entries.set(key, snapshot);
    this.lastKey = key;

    while (this.entries.size > this.MAX_ENTRIES) {
      const oldest_key = this.entries.keys().next().value;
      this.entries.delete(oldest_key);
    }
  }

  static get(technician_id: number, day_timestamp: number): TourDebugSnapshot | null {
    return this.entries.get(this.key(technician_id, day_timestamp)) || null;
  }

  /** Die zuletzt abgeschlossene Aufzeichnung — der Normalfall beim Öffnen des Debug-Fensters */
  static last(): TourDebugSnapshot | null {
    return this.lastKey ? this.entries.get(this.lastKey) || null : null;
  }

  /** Alle Aufzeichnungen, neueste zuerst */
  static all(): TourDebugSnapshot[] {
    return [...this.entries.values()].reverse();
  }

  static clear(): void {
    this.entries.clear();
    this.lastKey = null;
  }
}
